import EmailIcon from '@mui/icons-material/Email';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Inline, Stack } from '@/ui';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { PageBreadcrumbs } from '@/ui/layout/PageBreadcrumbs';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { Box, Link, Text } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { MouseEvent, PropsWithChildren, ReactElement, ReactNode } from 'react';

type PublicShellProps = PropsWithChildren<{
  background?: 'default' | 'surface';
  title?: ReactNode;
  actions?: ReactNode;
  pinFooterToViewport?: boolean;
}>;

type FooterLegalLink = {
  href: string;
  label: string;
  className?: string;
};

const footerLegalLinks: readonly FooterLegalLink[] = [
  { href: '/terms-of-service', label: 'Terms of Service' },
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/cookie-policy', label: 'Cookie Policy' },
  { href: '/acceptable-use-policy', label: 'Acceptable Use Policy' },
  { href: '#', label: 'Consent Preferences', className: 'termly-display-preferences' },
];

export const PublicShell = ({
  children,
  background = 'default',
  title = 'FlowQA UI',
  actions,
  pinFooterToViewport = true,
}: PublicShellProps): ReactElement => {
  const layoutMode = useLayoutMode();
  const { items: breadcrumbItems, visible: breadcrumbsVisible } = useBreadcrumbs();
  const showBreadcrumbs = breadcrumbsVisible && breadcrumbItems.length > 0;
  const shellPaddingX = layoutMode === layoutModeTokens.wide ? s('lg') : s('md');
  const headerPaddingX = s('md');
  const breadcrumbPaddingX = layoutMode === layoutModeTokens.wide ? s('md') : s('sm');
  const footerLinkSx = {
    color: 'text.secondary',
    textDecorationColor: 'currentColor',
    '&:hover': {
      color: 'text.secondary',
      textDecorationColor: 'currentColor',
    },
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  } as const;
  const footerTextSx = {
    lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
  } satisfies SxProps<Theme>;
  const footerLegalItemSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
  } satisfies SxProps<Theme>;
  const openConsentPreferences = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    document.getElementById('termly-pref-trigger')?.click();
  };
  const renderFooterLegalRow = (items: readonly FooterLegalLink[], key: string): ReactElement => (
    <Inline
      key={key}
      gap="none"
      align="center"
      justify="center"
      wrap="nowrap"
      sx={{ width: 'auto' }}
    >
      {items.map(({ href, label, className }, index) => (
        <Box key={`${key}-${href}-${label}`} component="span" sx={footerLegalItemSx}>
          {index > 0 ? (
            <Text
              role="body"
              component="span"
              color="textSecondary"
              sx={[footerTextSx, { mx: s('sm') }]}
            >
              ·
            </Text>
          ) : null}
          <Link
            component={href === '#' ? undefined : RouterLink}
            to={href === '#' ? undefined : href}
            href={href}
            target={href === '#' ? undefined : '_blank'}
            rel={href === '#' ? undefined : 'noopener noreferrer'}
            className={className}
            onClick={
              className === 'termly-display-preferences' ? openConsentPreferences : undefined
            }
            underline="hover"
            sx={[footerLinkSx, footerLegalItemSx]}
          >
            <Text role="body" component="span" sx={footerTextSx}>
              {label}
            </Text>
          </Link>
        </Box>
      ))}
    </Inline>
  );
  const [scrollReady, setScrollReady] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setScrollReady(true);
    }, 150);
    return () => {
      window.clearTimeout(id);
    };
  }, []);

  return (
    <Box
      sx={{
        height: '100dvh',
        backgroundColor: background === 'surface' ? 'background.paper' : 'background.default',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        borderTop: 'none',
        overflow: 'hidden',
      }}
    >
      <a className="flow-skip-link" href="#main-content">
        Skip to main content
      </a>
      <Box
        component="header"
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: theme.app.colors.surfaceAlt,
          borderBottom: `1px solid ${theme.app.colors.border}`,
          paddingTop: s('sm'),
          paddingBottom: s('xs'),
          boxSizing: 'border-box',
          width: '100%',
          transform: 'translateZ(0)',
          willChange: 'transform',
          display: 'flex',
          alignItems: 'center',
          gap: s('md'),
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: s('md'),
            minWidth: 0,
            width: '100%',
            px: headerPaddingX,
            boxSizing: 'border-box',
          }}
        >
          <Text role="sectionTitle">{title}</Text>
          <Box sx={{ marginLeft: 'auto' }}>{actions}</Box>
        </Box>
      </Box>
      <Box
        component="main"
        id="main-content"
        sx={{
          minHeight: 0,
          overflowY: scrollReady ? 'auto' : 'hidden',
          overflowX: 'hidden',
          overscrollBehavior: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showBreadcrumbs ? (
          <Box
            sx={(theme) => ({
              borderBottom: `1px solid ${theme.app.colors.border}`,
              paddingX: breadcrumbPaddingX,
              paddingTop: s('lg'),
              paddingBottom: s('md'),
            })}
          >
            <PageBreadcrumbs />
          </Box>
        ) : null}
        <Box sx={{ flex: '1 0 auto' }}>{children}</Box>
        <Box
          component="footer"
          sx={(theme) => ({
            backgroundColor: theme.app.colors.surfaceAlt,
            borderTop: `1px solid ${theme.app.colors.border}`,
            paddingX: shellPaddingX,
            paddingY: s('lg'),
            mt: pinFooterToViewport ? 'auto' : s('none'),
          })}
        >
          <Stack align="center" justify="center" gap="sm">
            <Stack gap="xs" alignItems="center" sx={{ width: 'auto' }}>
              <Stack
                gap="xs"
                alignItems="center"
                sx={{ display: { xs: 'flex', sm: 'none' }, width: 'auto' }}
              >
                {renderFooterLegalRow(footerLegalLinks.slice(0, 3), 'footer-legal-mobile-top')}
                {renderFooterLegalRow(footerLegalLinks.slice(3), 'footer-legal-mobile-bottom')}
              </Stack>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '100%' }}>
                {renderFooterLegalRow(footerLegalLinks, 'footer-legal-desktop')}
              </Box>
            </Stack>
            <Inline gap="md" align="center" justify="center" wrap="nowrap" sx={{ width: 'auto' }}>
              <Inline gap="sm" align="center" wrap="nowrap" sx={{ width: 'auto' }}>
                <EmailIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Link href="mailto:support@flowqa.com" underline="hover" sx={footerLinkSx}>
                  <Text role="body" component="span" sx={footerTextSx}>
                    support@flowqa.com
                  </Text>
                </Link>
              </Inline>
            </Inline>
            <Text role="body" color="textSecondary" sx={footerTextSx}>
              © {new Date().getFullYear()} FlowQA Ltd
            </Text>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
